import { InputHTMLAttributes, ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import OTPInput from "react-otp-input";
import {
  LuAlertCircle,
  LuArrowLeft,
  LuCheckCircle2,
  LuEye,
  LuEyeOff,
  LuLoader2,
} from "react-icons/lu";

/* ------------------------------------------------------------------ */
/* Heading                                                             */
/* ------------------------------------------------------------------ */

export const AuthHeading = ({
  title,
  description,
}: {
  title: string;
  description?: ReactNode;
}) => (
  <div className="mb-8">
    <h1 className="text-[1.75rem] leading-tight font-semibold tracking-tight text-honeConnectBlack">
      {title}
    </h1>
    {description && (
      <p className="mt-2 text-[15px] leading-relaxed text-honeConnectGray">
        {description}
      </p>
    )}
  </div>
);

export const BackLink = ({ to, label }: { to: string; label: string }) => (
  <Link
    to={to}
    className="mb-8 inline-flex items-center gap-2 rounded-md text-sm font-medium text-honeConnectGray transition-colors hover:text-honeConnectPurple focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40"
  >
    <LuArrowLeft className="h-4 w-4" aria-hidden />
    {label}
  </Link>
);

/* ------------------------------------------------------------------ */
/* Alerts                                                              */
/* ------------------------------------------------------------------ */

export const FormAlert = ({
  tone,
  message,
}: {
  tone: "error" | "success";
  message: string;
}) => {
  if (!message) return null;
  const isError = tone === "error";
  const Icon = isError ? LuAlertCircle : LuCheckCircle2;

  return (
    <div
      role={isError ? "alert" : "status"}
      className={`mb-6 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm leading-relaxed ${
        isError
          ? "border-red-200 bg-red-50 text-red-700"
          : "border-emerald-200 bg-emerald-50 text-emerald-800"
      }`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <span>{message}</span>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Text + password fields                                              */
/* ------------------------------------------------------------------ */

const fieldClass = (hasError: boolean, extra = "") =>
  `block w-full h-12 rounded-xl border bg-white px-4 text-[15px] text-honeConnectBlack placeholder:text-gray-400 transition-colors focus:outline-none focus:ring-4 disabled:bg-gray-50 disabled:text-gray-500 ${
    hasError
      ? "border-red-400 focus:border-red-500 focus:ring-red-500/10"
      : "border-gray-200 hover:border-gray-300 focus:border-honeConnectPurple focus:ring-honeConnectPurple/10"
  } ${extra}`;

type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "id"> & {
  id: string;
  label: string;
  error?: string;
  hint?: ReactNode;
  /** Optional element shown on the right of the label (e.g. "Forgot password?") */
  labelAside?: ReactNode;
};

const FieldShell = ({
  id,
  label,
  error,
  hint,
  labelAside,
  children,
}: Pick<FieldProps, "id" | "label" | "error" | "hint" | "labelAside"> & {
  children: ReactNode;
}) => (
  <div>
    <div className="mb-1.5 flex items-baseline justify-between gap-3">
      <label htmlFor={id} className="text-sm font-medium text-honeConnectBlack">
        {label}
      </label>
      {labelAside}
    </div>
    {children}
    {error ? (
      <p id={`${id}-error`} className="mt-1.5 text-xs text-red-600">
        {error}
      </p>
    ) : hint ? (
      <p id={`${id}-hint`} className="mt-1.5 text-xs text-gray-500">
        {hint}
      </p>
    ) : null}
  </div>
);

const describedBy = (id: string, error?: string, hint?: ReactNode) =>
  error ? `${id}-error` : hint ? `${id}-hint` : undefined;

export const TextField = ({
  id,
  label,
  error,
  hint,
  labelAside,
  ...input
}: FieldProps) => (
  <FieldShell id={id} label={label} error={error} hint={hint} labelAside={labelAside}>
    <input
      id={id}
      aria-invalid={!!error}
      aria-describedby={describedBy(id, error, hint)}
      className={fieldClass(!!error)}
      {...input}
    />
  </FieldShell>
);

export const PasswordField = ({
  id,
  label,
  error,
  hint,
  labelAside,
  ...input
}: FieldProps) => {
  const [visible, setVisible] = useState(false);

  return (
    <FieldShell id={id} label={label} error={error} hint={hint} labelAside={labelAside}>
      <div className="relative">
        <input
          id={id}
          type={visible ? "text" : "password"}
          aria-invalid={!!error}
          aria-describedby={describedBy(id, error, hint)}
          className={fieldClass(!!error, "pr-12")}
          {...input}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-gray-400 transition-colors hover:text-honeConnectPurple focus:outline-none focus-visible:text-honeConnectPurple"
        >
          {visible ? (
            <LuEyeOff className="h-5 w-5" aria-hidden />
          ) : (
            <LuEye className="h-5 w-5" aria-hidden />
          )}
        </button>
      </div>
    </FieldShell>
  );
};

/* ------------------------------------------------------------------ */
/* 6-digit code input (2FA + password reset)                           */
/* ------------------------------------------------------------------ */

export const CodeInput = ({
  value,
  onChange,
  error,
  autoFocus = true,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  autoFocus?: boolean;
}) => (
  <div>
    <OTPInput
      value={value}
      onChange={(v) => onChange(v.replace(/\D/g, ""))}
      numInputs={6}
      shouldAutoFocus={autoFocus}
      inputType="tel"
      skipDefaultStyles
      containerStyle="flex w-full justify-between gap-2 sm:gap-3"
      inputStyle={`h-12 sm:h-14 w-full min-w-0 rounded-xl border bg-white text-center text-xl font-semibold text-honeConnectBlack transition-colors focus:outline-none focus:ring-4 ${
        error
          ? "border-red-400 focus:border-red-500 focus:ring-red-500/10"
          : "border-gray-200 focus:border-honeConnectPurple focus:ring-honeConnectPurple/10"
      }`}
      renderInput={(props, index) => (
        <input {...props} aria-label={`Digit ${index + 1} of 6`} />
      )}
    />
    {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
  </div>
);

/* ------------------------------------------------------------------ */
/* Buttons                                                             */
/* ------------------------------------------------------------------ */

export const SubmitButton = ({
  loading,
  loadingText = "Please wait",
  children,
  disabled,
}: {
  loading: boolean;
  loadingText?: string;
  children: ReactNode;
  disabled?: boolean;
}) => (
  <button
    type="submit"
    disabled={loading || disabled}
    aria-busy={loading}
    className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-honeConnectPurple px-5 text-[15px] font-semibold text-white transition-colors hover:bg-hc_59077A focus:outline-none focus-visible:ring-4 focus-visible:ring-honeConnectPurple/30 disabled:cursor-not-allowed disabled:opacity-70"
  >
    {loading && <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden />}
    {loading ? loadingText : children}
  </button>
);

export const textLinkClass =
  "font-medium text-honeConnectPurple underline-offset-4 hover:underline focus:outline-none focus-visible:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline";
